<script setup lang="ts">
import { gsap } from 'gsap'

const props = defineProps<{
  slug: string
  accent: string
  /** Hero photo. Falls back to a branded icon panel when a type has none shot yet. */
  image?: string
  icon: string
}>()

const localePath = useLocalePath()
const section = ref<HTMLElement | null>(null)

let mm: ReturnType<typeof gsap.matchMedia> | null = null

onMounted(() => {
  const el = section.value
  if (!el) return

  // Ambient accent glow, like the soft radial blob behind bfm's static hero
  // pages — animated here with a slow breathing pulse. Gated to larger
  // screens with motion allowed: continuously animating a blurred element is
  // the kind of thing that thrashes the mobile compositor for little payoff.
  mm = gsap.matchMedia()
  mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => {
    gsap.to(el.querySelector('.bt-hero-glow'), {
      scale: 1.15,
      opacity: 0.75,
      duration: 5,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
    })
  })

  gsap.timeline({ defaults: { ease: 'power3.out' } })
    .fromTo(el.querySelector('.bt-hero-badge'),
      { opacity: 0, y: -20, scale: 0.85 },
      { opacity: 1, y: 0, scale: 1, duration: 0.55 })
    .fromTo(el.querySelector('.bt-hero-title'),
      { opacity: 0, y: 36 },
      { opacity: 1, y: 0, duration: 0.7, ease: 'power4.out' }, '-=0.25')
    .fromTo(el.querySelector('.bt-hero-subtitle'),
      { opacity: 0, y: 26 },
      { opacity: 1, y: 0, duration: 0.65 }, '-=0.35')
    .fromTo(el.querySelector('.bt-hero-cta'),
      { opacity: 0, y: 20, scale: 0.92 },
      { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: 'back.out(1.6)' }, '-=0.3')
    .fromTo(el.querySelector('.bt-hero-image'),
      { opacity: 0, y: 50, scale: 0.95, rotation: -2 },
      { opacity: 1, y: 0, scale: 1, rotation: 0, duration: 0.85, ease: 'back.out(1.2)' }, '-=0.5')
})

onUnmounted(() => mm?.revert())
</script>

<template>
  <section ref="section" class="pt-28 pb-14 md:pt-32 md:pb-18 lg:pt-40 lg:pb-24 bg-white relative overflow-hidden">
    <div class="absolute inset-0 bg-[radial-gradient(circle,_#d1d5db_1.5px,_transparent_1.5px)] [background-size:28px_28px] opacity-50 pointer-events-none" aria-hidden="true" />

    <div class="container relative grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      <div class="relative text-center lg:text-start">
        <!-- Centered behind the text column via top/left + negative margins
             (not `transform`, which the GSAP scale animation below drives
             itself). Drawn before the `relative` content wrapper so normal
             DOM-order stacking keeps the text on top, same as the section's
             own dot-grid/.container layering above. -->
        <div
          class="bt-hero-glow absolute top-1/2 left-1/2 -mt-56 -ml-56 md:-mt-80 md:-ml-80 w-[28rem] h-[28rem] md:w-[40rem] md:h-[40rem] rounded-full pointer-events-none"
          :style="{
            background: `radial-gradient(circle, ${props.accent}66 0%, transparent 62%)`,
            opacity: 0.55,
          }"
          aria-hidden="true"
        />

        <div class="relative">
          <div class="bt-hero-badge bfm-badge text-white px-3 md:px-4 py-1.5 md:py-2 text-sm md:text-base mb-4 lg:mb-6" :style="{ backgroundColor: props.accent }">
            {{ $t(`page.business_types.${props.slug}.badge`) }}
          </div>

          <h1 class="bt-hero-title text-[clamp(1.875rem,5vw,3.4rem)] font-bold leading-[1.15] rtl:leading-[1.4] text-deep-indigo mb-4 lg:mb-5">
            {{ $t(`page.business_types.${props.slug}.hero.title_pre`) }}
            <span :style="{ color: props.accent }">{{ $t(`page.business_types.${props.slug}.hero.title_accent`) }}</span>
          </h1>

          <p class="bt-hero-subtitle text-base md:text-lg lg:text-xl font-medium text-overcast-night leading-relaxed max-w-sm md:max-w-lg lg:max-w-140 mx-auto lg:mx-0 mb-3">
            {{ $t(`page.business_types.${props.slug}.hero.subtitle`) }}
          </p>
          <p class="bt-hero-subtitle text-sm md:text-base font-bold text-gray-500 mb-8 lg:mb-10">
            {{ $t(`page.business_types.${props.slug}.hero.note`) }}
          </p>

          <div class="bt-hero-cta">
            <a v-magnetic :href="localePath('/request-demo')" class="bfm-btn bfm-btn-md md:bfm-btn-lg bg-cheese-yellow py-2">
              {{ $t(`page.business_types.${props.slug}.hero.cta`) }}
            </a>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-center">
        <img
          v-if="props.image"
          :src="props.image"
          :alt="$t(`page.business_types.${props.slug}.name`)"
          class="bt-hero-image w-full max-w-sm sm:max-w-md lg:max-w-none rounded-2xl border-2 border-gray-900 object-cover aspect-[4/3]"
          :style="{ boxShadow: `10px 10px 0 ${props.accent}` }"
        >
        <!-- No hero photo shot for this type yet — a branded icon panel stands in. -->
        <div
          v-else
          class="bt-hero-image w-full max-w-sm sm:max-w-md lg:max-w-none rounded-2xl border-2 border-gray-900 aspect-[4/3] flex items-center justify-center"
          :style="{ background: `linear-gradient(135deg, ${props.accent}33, ${props.accent}99)`, boxShadow: `10px 10px 0 ${props.accent}` }">
          <img :src="props.icon" :alt="$t(`page.business_types.${props.slug}.name`)" class="w-1/3 h-1/3 opacity-90">
        </div>
      </div>
    </div>
  </section>
</template>
